import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../components/ui/Button";
import { useSiteContent } from "../lib/content";
import type { SiteContent } from "../lib/siteContent";
import { adminFieldClass, adminLabelClass } from "./adminFields";

export function AdminComoFunciona() {
  const { content, save, loading } = useSiteContent();
  const [items, setItems] = useState(content.howItWorks.items);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!loading) setItems(content.howItWorks.items);
  }, [content.howItWorks.items, loading]);

  if (loading) {
    return <p className="text-muted">A carregar…</p>;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const next: SiteContent = {
      ...content,
      howItWorks: {
        heading: String(form.get("heading") ?? ""),
        subtitle: String(form.get("subtitle") ?? ""),
        items: items.map((item, index) => ({
          n: String(form.get(`n-${index}`) ?? item.n),
          title: String(form.get(`title-${index}`) ?? item.title),
          text: String(form.get(`text-${index}`) ?? item.text),
        })),
      },
    };
    setSaving(true);
    const error = await save(next);
    setSaving(false);
    setOk(!error);
    setMessage(error ?? "Guardado. O site público já usa este texto.");
    if (!error) setItems(next.howItWorks.items);
  };

  return (
    <form onSubmit={onSubmit} className="max-w-3xl space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-primary">Como funciona</h1>
        <p className="mt-1 text-sm text-muted">Passos mostrados na página inicial.</p>
      </div>
      <label className={adminLabelClass}>
        Título da secção
        <input name="heading" className={adminFieldClass} defaultValue={content.howItWorks.heading} />
      </label>
      <label className={adminLabelClass}>
        Subtítulo
        <input name="subtitle" className={adminFieldClass} defaultValue={content.howItWorks.subtitle} />
      </label>

      <div className="space-y-4">
        {items.map((item, index) => (
          <fieldset key={`${item.n}-${index}`} className="rounded-xl border bg-surface p-4">
            <legend className="px-1 text-sm text-muted">Passo {index + 1}</legend>
            <div className="grid gap-3 sm:grid-cols-[6rem_1fr]">
              <label className={adminLabelClass}>
                Número
                <input name={`n-${index}`} className={adminFieldClass} defaultValue={item.n} />
              </label>
              <label className={adminLabelClass}>
                Título
                <input name={`title-${index}`} className={adminFieldClass} defaultValue={item.title} />
              </label>
            </div>
            <label className={`${adminLabelClass} mt-3`}>
              Texto
              <textarea name={`text-${index}`} rows={3} className={adminFieldClass} defaultValue={item.text} />
            </label>
            {items.length > 1 && (
              <button
                type="button"
                className="mt-3 text-sm text-urgent hover:underline"
                onClick={() => setItems(items.filter((_, i) => i !== index))}
              >
                Remover passo
              </button>
            )}
          </fieldset>
        ))}
      </div>

      <Button
        type="button"
        variant="secondary"
        onClick={() =>
          setItems([
            ...items,
            { n: String(items.length + 1).padStart(2, "0"), title: "Novo passo", text: "" },
          ])
        }
      >
        Adicionar passo
      </Button>

      {message && (
        <p className={ok ? "text-sm text-accent-terminal" : "text-sm text-urgent"}>{message}</p>
      )}
      <Button type="submit" disabled={saving}>
        {saving ? "A guardar…" : "Guardar"}
      </Button>
    </form>
  );
}
